import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography, Paper, Box, Grid, Chip, Button, CircularProgress, Alert, Divider,
} from '@mui/material';
import { Edit, ArrowBack, Warning } from '@mui/icons-material';
import axios from '../utils/api';
import { ORG_TYPE_LABELS, COST_TYPE_LABELS } from './serviceProviderShared';

const ServiceProviderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [provider, setProvider] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get(`/api/service-providers/${id}`);
        setProvider(response.data);
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Failed to load agency');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !provider) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Agency not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/management')}>Back to Management</Button>
      </Box>
    );
  }

  const orgTypes: string[] = provider.organizationTypes || [];
  const costTypes: string[] = provider.serviceCostTypes || [];

  return (
    <Paper sx={{ p: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1">{provider.organizationName}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip label="Agency" color="secondary" />
            <Chip
              label={provider.emailVerified ? 'Verified' : 'Unverified'}
              color={provider.emailVerified ? 'success' : 'warning'}
              size="small"
            />
            {provider.createdAt && (
              <Typography variant="body2" color="text.secondary">
                Registered {new Date(provider.createdAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/management')}>Back</Button>
          <Button variant="contained" startIcon={<Edit />} onClick={() => navigate(`/management/agency/edit/${provider.id}`)}>
            Edit
          </Button>
        </Box>
      </Box>

      {provider.duplicateFlagged && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
          <strong>Possible duplicate flagged for review.</strong> {provider.duplicateReason}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Organization Type</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            {orgTypes.map((t) => (
              <Chip key={t} size="small" label={ORG_TYPE_LABELS[t] || t} />
            ))}
            {orgTypes.includes('other') && provider.organizationTypeOther && (
              <Chip size="small" label={`Other: ${provider.organizationTypeOther}`} variant="outlined" />
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Services Offered At</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            {costTypes.map((t) => (
              <Chip key={t} size="small" label={COST_TYPE_LABELS[t] || t} color="primary" variant="outlined" />
            ))}
          </Box>
        </Grid>

        <Grid item xs={12}><Divider /></Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Address</Typography>
          <Typography variant="body1">
            {provider.addressLine1}{provider.addressLine2 ? `, ${provider.addressLine2}` : ''}<br />
            {provider.city}, {provider.state} {provider.zipCode}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
          <Typography variant="body1">{provider.phone}</Typography>
        </Grid>

        <Grid item xs={12}><Divider /></Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Person Registering</Typography>
          <Typography variant="body1">{provider.registrantName}</Typography>
          <Typography variant="body2" color="text.secondary">{provider.registrantEmail}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Public Contact Email</Typography>
          <Typography variant="body1">{provider.publicServicesEmail || provider.registrantEmail}</Typography>
        </Grid>

        <Grid item xs={12}><Divider /></Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Eligibility Criteria</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {provider.eligibilityCriteria || 'Not specified'}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="text.secondary">Jurisdiction</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {provider.serviceJurisdiction || 'Not specified'}
          </Typography>
        </Grid>

        <Grid item xs={12}><Divider /></Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary">Services Provided</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {provider.servicesProvided || 'Not specified'}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ServiceProviderDetail;
